import { memo, useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Flag, Edit2, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, Button, Label } from '@caruso/ui';

interface EndFlowData {
  label?: string;
  endStatus?: 'completed' | 'cancelled' | 'no_response' | 'error';
  endMessage?: string;
}

const statusOptions = [
  { value: 'completed', label: 'Completo', color: 'green', description: 'Fluxo finalizado com sucesso' },
  { value: 'cancelled', label: 'Cancelado', color: 'red', description: 'Fluxo cancelado pelo sistema ou usuário' },
  { value: 'no_response', label: 'Sem Resposta', color: 'orange', description: 'Cliente não respondeu no tempo limite' },
  { value: 'error', label: 'Erro', color: 'red', description: 'Fluxo encerrado devido a um erro' },
];

export const EndFlowNode = memo(({ data, id }: NodeProps<EndFlowData>) => {
  const { deleteElements, setNodes } = useReactFlow();
  const [isEditing, setIsEditing] = useState(false);
  const [endStatus, setEndStatus] = useState<'completed' | 'cancelled' | 'no_response' | 'error'>(data.endStatus || 'completed');
  const [endMessage, setEndMessage] = useState(data.endMessage || '');

  const handleDelete = () => {
    deleteElements({ nodes: [{ id }] });
  };

  const handleSave = () => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              endStatus,
              endMessage,
            },
          };
        }
        return node;
      })
    );
    setIsEditing(false);
  };

  const selectedStatus = statusOptions.find((s) => s.value === endStatus) || statusOptions[0];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'cancelled':
        return 'red';
      case 'no_response':
        return 'orange';
      case 'error':
        return 'red';
      default:
        return 'gray';
    }
  };

  const color = getStatusColor(endStatus);

  return (
    <>
      <div className={`bg-${color}-500/10 border-2 border-${color}-500 rounded-lg p-4 min-w-[250px] shadow-lg`}
           style={{ backgroundColor: `rgb(var(--${color}-500) / 0.1)`, borderColor: `var(--${color}-500)` }}>
        <Handle type="target" position={Position.Top} className={`!bg-${color}-500`} />

        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center`}
                 style={{ backgroundColor: color === 'green' ? '#22c55e' : color === 'red' ? '#ef4444' : color === 'orange' ? '#f97316' : '#6b7280' }}>
              <Flag className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-bold" style={{ color: color === 'green' ? '#22c55e' : color === 'red' ? '#ef4444' : color === 'orange' ? '#f97316' : '#6b7280' }}>
                Finalizar Fluxo
              </div>
              <div className="text-xs text-gray-500">Encerra a execução</div>
            </div>
          </div>

          <div className="flex gap-1">
            <button
              onClick={() => setIsEditing(true)}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title="Editar"
            >
              <Edit2 className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={handleDelete}
              className="p-1.5 hover:bg-red-100 rounded transition-colors"
              title="Excluir"
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          </div>
        </div>

        <div className="space-y-2 text-sm">
          <div className={`p-2 rounded border`}
               style={{
                 backgroundColor: color === 'green' ? 'rgba(34, 197, 94, 0.1)' :
                                  color === 'red' ? 'rgba(239, 68, 68, 0.1)' :
                                  color === 'orange' ? 'rgba(249, 115, 22, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                 borderColor: color === 'green' ? 'rgba(34, 197, 94, 0.3)' :
                              color === 'red' ? 'rgba(239, 68, 68, 0.3)' :
                              color === 'orange' ? 'rgba(249, 115, 22, 0.3)' : 'rgba(107, 114, 128, 0.3)'
               }}>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Status:</span>
              <span className="font-medium" style={{ color: color === 'green' ? '#22c55e' : color === 'red' ? '#ef4444' : color === 'orange' ? '#f97316' : '#6b7280' }}>
                {selectedStatus.label}
              </span>
            </div>
          </div>

          {endMessage && (
            <div className="p-2 bg-white/50 rounded text-xs text-gray-600 border border-gray-200">
              <span className="font-medium">Mensagem:</span>
              <p className="mt-1 italic">{endMessage}</p>
            </div>
          )}
        </div>

        {/* Sem handle de saída - é um nó final */}
      </div>

      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="bg-brand-ink border-brand-gold/20 text-brand-ivory max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-brand-gold flex items-center gap-2">
              <Flag className="w-5 h-5" />
              Configurar Finalização do Fluxo
            </DialogTitle>
            <DialogDescription className="text-brand-ivory/60">
              Defina o status final e mensagem opcional para o encerramento do fluxo
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-brand-ivory/80">
                Status de Finalização <span className="text-red-400">*</span>
              </Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {statusOptions.map((status) => (
                  <label
                    key={status.value}
                    className={`cursor-pointer p-3 rounded border transition-all ${
                      endStatus === status.value
                        ? status.color === 'green'
                          ? 'bg-green-500/20 border-green-500'
                          : status.color === 'orange'
                          ? 'bg-orange-500/20 border-orange-500'
                          : 'bg-red-500/20 border-red-500'
                        : 'bg-brand-ink/50 border-brand-gold/20 hover:border-brand-gold/40'
                    }`}
                  >
                    <input
                      type="radio"
                      name="endStatus"
                      value={status.value}
                      checked={endStatus === status.value}
                      onChange={() => setEndStatus(status.value as any)}
                      className="hidden"
                    />
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-3 h-3 rounded-full ${
                          status.color === 'green'
                            ? 'bg-green-500'
                            : status.color === 'orange'
                            ? 'bg-orange-500'
                            : 'bg-red-500'
                        }`}
                      />
                      <span className="font-medium text-brand-ivory">{status.label}</span>
                    </div>
                    <p className="text-xs text-brand-ivory/50 mt-1">{status.description}</p>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-brand-ivory/80">Mensagem Final (opcional)</Label>
              <textarea
                value={endMessage}
                onChange={(e) => setEndMessage(e.target.value)}
                placeholder="Mensagem opcional a ser enviada antes de finalizar o fluxo..."
                rows={3}
                className="w-full mt-1 p-3 bg-brand-ink/50 border border-brand-gold/20 text-brand-ivory rounded resize-none"
              />
              <p className="text-xs text-brand-ivory/40 mt-1">
                Se preenchida, esta mensagem será enviada ao cliente antes de encerrar o fluxo
              </p>
            </div>

            <div className="mt-4 p-3 bg-brand-gold/5 rounded border border-brand-gold/20">
              <p className="text-xs text-brand-ivory/60 mb-2 font-medium">Sobre os Status:</p>
              <ul className="text-xs text-brand-ivory/50 space-y-1 list-disc list-inside">
                <li><strong className="text-green-400">Completo:</strong> Fluxo concluído com sucesso (meta atingida)</li>
                <li><strong className="text-red-400">Cancelado:</strong> Fluxo interrompido (usuário cancelou ou desistiu)</li>
                <li><strong className="text-orange-400">Sem Resposta:</strong> Cliente não respondeu (timeout)</li>
                <li><strong className="text-red-400">Erro:</strong> Erro durante execução do fluxo</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button
              variant="outline"
              onClick={() => setIsEditing(false)}
              className="border-brand-gold/20 text-brand-ivory hover:bg-brand-gold/10"
            >
              Cancelar
            </Button>
            <Button
              variant="premium"
              onClick={handleSave}
              className="bg-green-500 hover:bg-green-600"
            >
              Salvar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

EndFlowNode.displayName = 'EndFlowNode';
